import React from "react";
import { filmstripCell } from "@veyso/core";
import { timecode } from "../components/ui.js";

export interface Filmstrip {
  key: string;
  width: number;
  height: number;
  /**
   * The recording's length, which is what the sheet's grid was laid out for.
   *
   * Not the body's. After a cut the two differ, and deriving the grid from the
   * body produced the wrong number of rows and a strip of squashed frames.
   */
  durationSeconds: number;
}

export interface SyncPointProps {
  index: number;
  /** Seconds into the recording, which is what the strip is indexed by. */
  seconds: number;
  durationSeconds: number;
  filmstrip: Filmstrip | null;
  onClick: () => void;
}

const THUMB_WIDTH = 34;

/**
 * The marker where a line of narration meets a moment in the recording.
 *
 * It carries the frame it points at rather than only a timecode, because a
 * timecode has to be looked up and a picture does not — which is the whole
 * reason for putting the script beside the video in the first place.
 *
 * Sits inline in the prose, so it uses `inline-flex` and is marked
 * non-editable: the text around it is a contenteditable region, and without
 * that the chip could be typed into or deleted a character at a time.
 */
export const SyncPoint: React.FC<SyncPointProps> = ({ index, seconds, durationSeconds, filmstrip, onClick }) => {
  const cell = filmstrip
    ? filmstripCell(seconds, durationSeconds, filmstrip.width, filmstrip.height, THUMB_WIDTH)
    : null;

  return (
    <button
      contentEditable={false}
      onClick={onClick}
      title={`Sync point ${index} — jump to ${timecode(seconds)}`}
      className="mx-0.5 inline-flex translate-y-[3px] select-none items-center gap-1 rounded-[5px] border border-[var(--edge-strong)] bg-bay-750 py-[2px] pl-[2px] pr-1.5 align-baseline transition-colors hover:border-scope-400/60 hover:bg-bay-700"
    >
      <span
        aria-hidden
        className="block shrink-0 overflow-hidden rounded-[3px] bg-bay-900 bg-no-repeat"
        style={
          cell
            ? {
                width: THUMB_WIDTH,
                height: Math.round(THUMB_WIDTH / cell.aspectRatio),
                backgroundImage: `url(/assets/${filmstrip!.key})`,
                backgroundSize: cell.backgroundSize,
                backgroundPosition: cell.backgroundPosition,
              }
            : { width: THUMB_WIDTH, height: 19 }
        }
      />
      <span className="tnum text-[10px] font-semibold leading-none text-ink-300">{index}</span>
    </button>
  );
};
